import { useEffect, useMemo, useRef, useState } from 'react';
import { gql, useLazyQuery, useQuery } from '@apollo/client';
import { Loader2, Search, X } from 'lucide-react';
import { Input } from '@artstorefronts/ui';

/**
 * MEMBER QUEST LOOKUP (Nick, 2026-09-01): search a user by name or email right on the
 * quests admin, and see their whole quest world — which quests their criteria make them
 * eligible for, which they've opted into, steps complete out of possible, and what each
 * quest has paid them. The report is read-only on the server by construction: looking at
 * a member never enrolls, stamps, or pays.
 */

const SEARCH_USERS = gql`
  query QuestAdminUserSearch($search: String) {
    users(search: $search, limit: 8) {
      users {
        asfId
        firstName
        lastName
        email
      }
    }
  }
`;

const USER_QUEST_REPORT = gql`
  query AdminUserQuestReport($userId: ID!) {
    adminUserQuestReport(userId: $userId) {
      intents
      careerStage
      countryCode
      rows {
        quest {
          id
          slug
          name
          questType
          assignment
          priority
          pointsReward
        }
        eligible
        eligibility
        serving
        state
        source
        completedAt
        score
        stepsComplete
        stepsTotal
        pointsEarned
      }
    }
  }
`;

type FoundUser = { asfId: string; firstName: string | null; lastName: string | null; email: string };

type ReportRow = {
  quest: {
    id: string;
    slug: string;
    name: string;
    questType: string | null;
    assignment: string;
    priority: number;
    pointsReward: number;
  };
  eligible: boolean;
  eligibility: string;
  serving: boolean;
  state: 'active' | 'completed' | 'retired' | null;
  source: string | null;
  completedAt: string | null;
  score: number;
  stepsComplete: number;
  stepsTotal: number;
  pointsEarned: number;
};

type Report = {
  intents: string[];
  careerStage: string | null;
  countryCode: string | null;
  rows: ReportRow[];
};

function userLabel(user: FoundUser): string {
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return name || user.email;
}

function lookupInputValue(selected: FoundUser | null, term: string): string {
  if (!selected) return term;
  const label = userLabel(selected);
  if (label === selected.email) return selected.email;
  return `${label} — ${selected.email}`;
}

function statusCell(row: ReportRow): { text: string; classes: string } {
  if (row.state === 'completed') return { text: 'Completed', classes: 'bg-green-100 text-green-800' };
  if (row.state === 'retired') return { text: 'Left', classes: 'bg-gray-100 text-gray-600' };
  if (row.state === 'active')
    return row.source === 'manual'
      ? { text: 'Opted in', classes: 'bg-indigo-100 text-indigo-700' }
      : { text: 'On it (auto)', classes: 'bg-blue-100 text-blue-700' };
  return { text: 'Not joined', classes: 'bg-gray-50 text-gray-400' };
}

export function UserQuestLookup() {
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [selected, setSelected] = useState<FoundUser | null>(null);
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(term.trim()), 300);
    return () => window.clearTimeout(handle);
  }, [term]);

  const [runSearch, { data: searchData, loading: searching }] = useLazyQuery<{ users: { users: FoundUser[] } }>(
    SEARCH_USERS,
    { fetchPolicy: 'network-only' }
  );

  useEffect(() => {
    if (debounced.length >= 2 && !selected) {
      void runSearch({ variables: { search: debounced } });
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [debounced, selected, runSearch]);

  // Click-away closes the dropdown, not the whole lookup.
  useEffect(() => {
    const onDown = (event: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const { data: reportData, loading: reportLoading } = useQuery<{ adminUserQuestReport: Report }>(USER_QUEST_REPORT, {
    variables: { userId: selected?.asfId ?? '' },
    skip: !selected,
    fetchPolicy: 'network-only',
  });

  const matches = useMemo(() => searchData?.users.users ?? [], [searchData]);
  const report = reportData?.adminUserQuestReport ?? null;

  return (
    <div className="mb-8 rounded-lg border border-gray-200 bg-white p-4" data-testid="user-quest-lookup">
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-sm font-semibold text-gray-900">Look up a member</p>
        <div ref={boxRef} className="relative w-full max-w-md">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            className="pl-8"
            placeholder="Search by name or email…"
            value={lookupInputValue(selected, term)}
            readOnly={Boolean(selected)}
            data-testid="user-quest-search"
            onChange={(event) => setTerm(event.target.value)}
            onFocus={() => {
              if (!selected && matches.length > 0) setOpen(true);
            }}
          />
          {selected ? (
            <button
              type="button"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
              aria-label="Clear selected member"
              data-testid="user-quest-clear"
              onClick={() => {
                setSelected(null);
                setTerm('');
              }}
            >
              <X className="h-4 w-4" />
            </button>
          ) : null}
          {open && !selected ? (
            <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg">
              {searching ? (
                <div className="flex items-center gap-2 px-3 py-2 text-sm text-gray-500">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Searching…
                </div>
              ) : matches.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500">No members match “{debounced}”.</div>
              ) : (
                matches.map((user) => (
                  <button
                    key={user.asfId}
                    type="button"
                    className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-50"
                    data-testid={`user-quest-option-${user.asfId}`}
                    onClick={() => {
                      setSelected(user);
                      setOpen(false);
                    }}
                  >
                    <span className="font-medium text-gray-900">{userLabel(user)}</span>
                    <span className="ml-2 text-xs text-gray-500">{user.email}</span>
                    <span className="ml-2 text-xs text-gray-400">#{user.asfId}</span>
                  </button>
                ))
              )}
            </div>
          ) : null}
        </div>
      </div>

      {selected ? (
        reportLoading && !report ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
          </div>
        ) : report ? (
          <div className="mt-4" data-testid="user-quest-report">
            <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
              <span className="font-semibold uppercase tracking-wide text-gray-500">Their criteria:</span>
              {report.intents.length > 0 ? (
                report.intents.map((intent) => (
                  <span key={intent} className="rounded-full bg-emerald-50 px-2 py-0.5 font-medium text-emerald-700">
                    {intent}
                  </span>
                ))
              ) : (
                <span className="text-gray-400">no intents set</span>
              )}
              {report.careerStage ? (
                <span className="rounded-full bg-sky-50 px-2 py-0.5 font-medium text-sky-700">{report.careerStage}</span>
              ) : null}
              {report.countryCode ? (
                <span className="rounded-full bg-violet-50 px-2 py-0.5 font-medium text-violet-700">
                  {report.countryCode}
                </span>
              ) : null}
            </div>

            <div className="mt-3 overflow-x-auto">
              <div className="min-w-[760px]">
                <div className="grid grid-cols-[1.2fr_1.4fr_120px_90px_110px] items-center gap-2 border-b px-2 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  <span>Quest</span>
                  <span>Eligibility</span>
                  <span>Status</span>
                  <span>Steps</span>
                  <span>Points paid</span>
                </div>
                {report.rows.map((row) => {
                  const status = statusCell(row);
                  return (
                    <div
                      key={row.quest.id}
                      className="grid grid-cols-[1.2fr_1.4fr_120px_90px_110px] items-center gap-2 border-b px-2 py-2.5 text-sm"
                      data-testid={`user-quest-row-${row.quest.slug}`}
                    >
                      <span className="truncate">
                        <span className="font-medium text-gray-900">{row.quest.name}</span>
                        {row.serving ? (
                          <span className="ml-2 rounded-full bg-green-600 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
                            In their band
                          </span>
                        ) : null}
                      </span>
                      <span className={row.eligible ? 'text-emerald-700' : 'text-gray-500'}>{row.eligibility}</span>
                      <span>
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${status.classes}`}>
                          {status.text}
                        </span>
                      </span>
                      <span className="text-gray-700">
                        {row.stepsComplete}
                        <span className="text-xs text-gray-400"> / {row.stepsTotal}</span>
                        <span className="ml-1 text-xs text-gray-400">({row.score}%)</span>
                      </span>
                      <span className={row.pointsEarned > 0 ? 'font-medium text-amber-700' : 'text-gray-400'}>
                        {row.state === 'completed'
                          ? row.pointsEarned > 0
                            ? `⭐ ${row.pointsEarned.toLocaleString()}`
                            : '0'
                          : '—'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
            <p className="mt-2 text-xs text-gray-400">
              Eligibility uses the same audience matchers serving uses. “Points paid” shows once they complete a quest
              — the completion bonus plus the step and buddy points that quest paid along the way. Looking someone up
              never enrolls or pays anything.
            </p>
          </div>
        ) : null
      ) : null}
      {selected && !reportLoading && !report ? (
        <p className="mt-3 text-sm text-red-600">Couldn’t load this member’s quest report.</p>
      ) : null}
    </div>
  );
}
